"use client";

import React, { useContext, useEffect, useMemo, useRef, useState, ReactNode } from "react";
import Fab from "@mui/material/Fab";
import { Box, Grow, Stack, ClickAwayListener, useMediaQuery, alpha, useTheme, Tooltip, Zoom, Typography, Fade } from "@mui/material";
import Chat from "./Chat";
import { ChatContext } from "./context";
import { useWidgetSettingsContext } from "./settings";
import chroma from "chroma-js";
import Iconify from "./components/iconify";
import Image from "./components/image";
import { safePostMessage, safeDocumentStyleModification } from "../shadow-dom-utils";

// Custom Tooltip Component
interface CustomTooltipProps {
  open: boolean;
  title: ReactNode;
  children: ReactNode;
  placement?: "left" | "right" | "top";
  onClose?: () => void;
  onClick?: () => void;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  open,
  title,
  children,
  placement = "right",
  onClose,
  onClick,
}) => {
  let tooltipStyle: any = {
    position: "absolute",
    zIndex: 2000,
    backgroundColor: "#fff",
    color: "#222",
    borderRadius: "10px",
    border: "1px solid #C4CDD5",
    boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
    padding: "10px 16px",
    minWidth: 160,
    fontWeight: 500,
    fontSize: 15,
    whiteSpace: "nowrap",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  };

  if (placement === "right") {
    tooltipStyle.top = "50%";
    tooltipStyle.left = "calc(100% + 8px)";
    tooltipStyle.transform = "translateY(-50%)";
  } else if (placement === "left") {
    tooltipStyle.top = "50%";
    tooltipStyle.right = "calc(100% + 8px)";
    tooltipStyle.transform = "translateY(-50%)";
  } else if (placement === "top") {
    tooltipStyle.bottom = "calc(100% + 8px)";
    tooltipStyle.left = "50%";
    tooltipStyle.transform = "translateX(-50%)";
  }

  const handleTooltipClick = () => {
    if (onClick) {
      onClick();
    }
    if (onClose) {
      onClose();
    }
  };

  return (
    <Box sx={{ position: "relative", display: "inline-block" }}>
      {children}
      {open && (
        <Box sx={tooltipStyle} onClick={handleTooltipClick}>
          <Typography variant="body2">{title}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default function WidgetChat(): JSX.Element {
  const { setChatOpen, editMode, isHome, chatOpen } = useContext(ChatContext);
  const isMobile = useMediaQuery("(max-width: 600px)");
  const elementRef = useRef(null);
  const theme = useTheme();
  const settings = useWidgetSettingsContext();

  const [showTooltip, setShowTooltip] = useState<boolean>(true);

  const fabBoxStyle = useMemo(
    () => ({
      position: "fixed",
      zIndex: 999,
      ...(settings.position === "left"
        ? {
          left: settings.sideSpacing,
        }
        : {
          right: settings.sideSpacing,
        }),

      bottom: settings.bottomSpacing,
      // FIXED: Add pointer-events handling
      pointerEvents: "auto",
      "& .MuiFab-root": {
        backgroundColor: settings.laucherBgColor!,
        " &:hover, &:active": {
          backgroundColor: alpha(settings.laucherBgColor!, 0.8),
        },
      },
    }),
    [theme, settings]
  );

  const chatBoxStyle = useMemo(() => {
    if (isMobile) {
      return {
        position: "fixed",
        zIndex: chatOpen ? 1200 : -1,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100vw",
        height: "100dvh",
        maxWidth: "100vw",
        minWidth: 0,
        margin: 0,
        padding: 0,
        pointerEvents: "auto",
        display: chatOpen ? "block" : "none",
      };
    }
    // Only include responsive values and properties supported by MUI's sx prop
    const desktopStyle: any = {
      position: "fixed",
      zIndex: chatOpen ? 1200 : -1,
      bottom: {
        xs: 0,
        sm: 76 + settings.bottomSpacing,
        md: 86 + settings.bottomSpacing,
      },
      minWidth: {
        sm: 300,
      },
      maxWidth: 600,
      width: {
        xs: "100vw",
        sm: 0.4,
        md: 0.3,
      },
      pointerEvents: "auto",
      display: chatOpen ? "block" : "none",
    };
    if (settings.position === "left") {
      desktopStyle.left = {
        xs: 0,
        sm: settings.sideSpacing,
      };
    } else {
      desktopStyle.right = {
        xs: 0,
        sm: settings.sideSpacing,
      };
    }
    return desktopStyle;
  }, [settings, chatOpen, isMobile]);

  const handleClickAway = () => {
    if (!editMode && !isHome) {
      setChatOpen(false);
    }
  };

  const handleFabClick = () => {
    setChatOpen(prev => !prev);
    setShowTooltip(false);
  };

  useEffect(() => {
    // When the widget is opened
    if (chatOpen) {
      safePostMessage({ widgetState: "open", height: "700px", width: "350px" });

      // Only modify document styles if we're not in Shadow DOM
      if (window.innerWidth < 600) {
        safeDocumentStyleModification("overflow: hidden !important");
      }
    } else {
      safePostMessage({ widgetState: "closed", height: "56px", width: "56px" });
      safeDocumentStyleModification("overflow: auto !important");
    }

    // When the widget is closed
  }, [chatOpen]);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ pointerEvents: "auto" }} data-widget-component="main-container">
        <Stack id="chat-widget-container" sx={chatBoxStyle} data-widget-component="chat-container">
          <Grow
            in={chatOpen}
            style={{
              transformOrigin: "bottom right",
            }}
          >
            <Box sx={{ width: 1 }}>
              <Chat />
            </Box>
          </Grow>
        </Stack>
        <Box id="chat-widget-fab" sx={fabBoxStyle} ref={elementRef} data-widget-component="fab-container">
          {isMobile && !chatOpen && (
            <CustomTooltip
              open={showTooltip}
              title={settings.tooltipTitle || "Hi👋, Chat with us!"}
              placement={"top"}
              onClose={() => setShowTooltip(false)}
              onClick={handleFabClick}
            >
              <Grow in style={{ transformOrigin: "top left" }}>
                <Fab
                  onClick={handleFabClick}
                  sx={{
                    p: 1,
                  }}
                >
                  {chatOpen ? (
                    <Iconify
                      icon={"material-symbols:close-rounded"}
                      sx={{
                        color:
                          chroma(settings.laucherBgColor || "white").luminance() > 0.5
                            ? "#000"
                            : "white",
                      }}
                    />
                  ) : (settings?.launcherIcon as any)?.preview ? (
                    <Image
                      sx={{
                        borderRadius: "100%",
                        aspectRatio: 1,
                        width: 1,
                      }}
                      src={(settings?.launcherIcon as any)?.preview}
                    />
                  ) : (
                    <Iconify
                      icon={"ic:round-chat"}
                      sx={{
                        color:
                          chroma(settings.laucherBgColor || "white").luminance() > 0.5
                            ? "#000"
                            : "#fff",
                      }}
                    />
                  )}
                </Fab>
              </Grow>
            </CustomTooltip>
          )}

          {!isMobile && (
            <CustomTooltip
              open={showTooltip}
              title={
                <Box sx={{ textAlign: "center", padding: 1 }}>
                  <Typography variant="body2" sx={{
                    fontWeight: 500,
                    color: "black",
                    maxWidth: 200,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {settings.tooltipTitle || "Hi👋, Chat with us!"}
                  </Typography>
                </Box>
              }
              placement={settings.position === "left" ? "right" : "left"}
              onClose={() => setShowTooltip(false)}
              onClick={handleFabClick}
            >
              <Grow in style={{ transformOrigin: "top left" }}>
                <Fab
                  onClick={handleFabClick}
                  sx={{
                    p: 1,
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.1)",
                    },
                  }}
                >
                  {chatOpen ? (
                    <Iconify
                      icon={"material-symbols:close-rounded"}
                      sx={{
                        color:
                          chroma(settings.laucherBgColor || "white").luminance() > 0.5
                            ? "#000"
                            : "#fff",
                      }}
                    />
                  ) : (settings?.launcherIcon as any)?.preview ? (
                    <Image
                      sx={{
                        borderRadius: "100%",
                        aspectRatio: 1,
                        width: 1,
                      }}
                      src={(settings?.launcherIcon as any)?.preview}
                    />
                  ) : (
                    <Iconify
                      icon={"ic:round-chat"}
                      sx={{
                        color:
                          chroma(settings.laucherBgColor || "white").luminance() > 0.5
                            ? "#000"
                            : "#fff",
                      }}
                    />
                  )}
                </Fab>
              </Grow>
            </CustomTooltip>
          )}
        </Box>
      </Box>
    </ClickAwayListener>
  );
}